import { useCallback } from 'react'
import { toast } from 'react-toastify'
import { API, API_ROUTER } from '../../../api'
import {
  isFieldEmpty,
  isFieldEmptyAndLength,
  isSelectArrayEmpty,
  isUserUrlChangeValid,
  isValidFacebookUrl,
  addProtocol,
  dataUriToBlob,
} from '../../../helpers'
import {
  FORM_CONSTANTS,
  MEDIA_CONSTANTS,
  SOCIAL_MEDIA,
  INITIAL_FORM_ERRORS,
} from '../config'
import { isDateValid } from '../../team-create-change/const'

export const useFormHandlers = (
  formFields,
  setFormFields,
  setErrors,
  loadedImg,
  setSectionsToExpand
) => {
  const inputHandler = useCallback(
    e => {
      const { name, value } = e.target
      setFormFields(prev => ({ ...prev, [name]: value }))
    },
    [setFormFields]
  )

  const selectHandler = useCallback(
    (value, selectName) => {
      setFormFields(prev => ({ ...prev, [selectName]: value }))
    },
    [setFormFields]
  )

  const onLanguageChange = useCallback(
    value => {
      const lang = FORM_CONSTANTS.LANGUAGE_KEY
      const [langObject] = value

      localStorage.setItem('interfaceLang', langObject.value)
      setFormFields(prev => ({ ...prev, [lang]: langObject.value }))
    },
    [setFormFields]
  )

  const validateForm = useCallback(() => {
    const newErrors = { ...INITIAL_FORM_ERRORS }

    newErrors.firstName = isFieldEmpty(formFields.firstName)
    newErrors.lastName = isFieldEmpty(formFields.lastName)
    newErrors.nickname = isFieldEmpty(formFields.nickname)
    newErrors.nickname = isFieldEmptyAndLength(formFields.nickname)
    newErrors.citizenship = isSelectArrayEmpty(formFields.citizenship)
    newErrors.discord = isFieldEmpty(formFields.discord)
    newErrors.country = isSelectArrayEmpty(formFields.country)

    // Gender is optional, so don't validate it as required
    // Clear any existing gender error
    newErrors.gender = ''

    newErrors.url = isUserUrlChangeValid(formFields.url)
    newErrors.birthDate = isDateValid(formFields.birthDate, true)

    // Validate Facebook URL
    newErrors.facebookLink = isValidFacebookUrl(formFields.facebookLink)
      ? ''
      : 'Please enter a valid Facebook URL'

    setErrors(prev => ({ ...prev, ...newErrors }))

    // Check which sections have errors and expand them
    const hasErrors = Object.values(newErrors).some(value => value.length)

    if (hasErrors && setSectionsToExpand) {
      const sectionsToExpand = []

      // Personal Info section fields
      if (
        newErrors.firstName ||
        newErrors.lastName ||
        newErrors.nickname ||
        newErrors.birthDate ||
        newErrors.citizenship
      ) {
        sectionsToExpand.push('personalInfo')
      }

      // Contact Info section fields
      if (
        newErrors.discord ||
        newErrors.country ||
        newErrors.url ||
        newErrors.facebookLink
      ) {
        sectionsToExpand.push('contactInfo')
      }

      setSectionsToExpand(sectionsToExpand)
    }

    return !hasErrors
  }, [formFields, setErrors, setSectionsToExpand])

  const prepareFormData = useCallback(() => {
    let bod = null
    if (formFields.birthDate) {
      bod = new Date(formFields.birthDate)
      bod = bod.toISOString().split('T')[0]
    }

    // Validate and sanitize Facebook URL
    const getValidFacebookUrl = url => {
      if (!url || url.trim() === '') {
        return SOCIAL_MEDIA.FACEBOOK.DEFAULT_URL
      }

      const trimmedUrl = url.trim()
      const fullUrl = addProtocol(trimmedUrl)

      return isValidFacebookUrl(trimmedUrl)
        ? fullUrl
        : SOCIAL_MEDIA.FACEBOOK.DEFAULT_URL
    }

    return {
      ...API_ROUTER.user.setUserData,
      data: {
        firstName: formFields.firstName,
        lastName: formFields.lastName,
        nickname: formFields.nickname,
        discord: formFields.discord,
        birthDate: bod,
        gender:
          formFields.gender && formFields.gender[0]
            ? formFields.gender[0].value
            : null,
        language: Array.isArray(formFields.language)
          ? formFields.language[0]?.value || FORM_CONSTANTS.DEFAULT_LANGUAGE
          : formFields.language || FORM_CONSTANTS.DEFAULT_LANGUAGE,
        languages: [],
        citizenship:
          formFields.citizenship && formFields.citizenship[0]
            ? formFields.citizenship[0].value
            : null,
        country:
          formFields.country && formFields.country[0]
            ? formFields.country[0].value
            : null,
        url: formFields.url,
        about: formFields.about,
        phone: '+7 900 505 55 55',
        sites: [
          {
            site: SOCIAL_MEDIA.FACEBOOK.SITE_NAME,
            url: getValidFacebookUrl(formFields.facebookLink),
          },
        ],
      },
    }
  }, [formFields])

  const uploadAvatar = useCallback(
    async userId => {
      if (!loadedImg) return null

      const blob = dataUriToBlob(loadedImg)
      const formData = new FormData()
      formData.append(MEDIA_CONSTANTS.FORM_DATA_KEYS.MEDIA, blob)
      formData.append(
        MEDIA_CONSTANTS.FORM_DATA_KEYS.MEDIA_TYPE,
        MEDIA_CONSTANTS.USER_AVATAR
      )
      formData.append(MEDIA_CONSTANTS.FORM_DATA_KEYS.MEDIA_OWNER_ID, userId)

      return API.request(
        {
          ...API_ROUTER.media.create,
          headers: {
            'Content-type': 'multipart/form-data',
          },
          data: formData,
        },
        true
      )
    },
    [loadedImg]
  )

  const onSubmit = useCallback(
    async e => {
      e.preventDefault()

      if (!validateForm()) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
        return
      }

      try {
        const params = prepareFormData()
        const resp = await API.request(params, true)

        if (loadedImg) {
          await uploadAvatar(resp.id)
        }

        toast.success('Profile updated')
        window.location.href = '/profile/settings'
      } catch (err) {
        if (err.data?.errors?.nickname) {
          toast.error('Nickname ' + err.data.errors.nickname)
        } else {
          toast.error(err.data?.message || 'An error occurred')
        }
      }
    },
    [validateForm, prepareFormData, uploadAvatar, loadedImg]
  )

  const activateAccount = useCallback(async () => {
    try {
      const res = await API.request({
        ...API_ROUTER.user.activateAccount,
      })
      toast.success(res.message)
    } catch (err) {
      console.log(err)
      toast.error('Failed to activate account')
    }
  }, [])

  const changePassword = useCallback(() => {
    window.location.href = '/password/change'
  }, [])

  return {
    inputHandler,
    selectHandler,
    onLanguageChange,
    onSubmit,
    activateAccount,
    changePassword,
  }
}

export default useFormHandlers
